import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { queryClient } from './lib/query-client';
import type { RouterContext } from './lib/router-context';
import { routeTree } from './routeTree.gen';
import './styles.css';

// ── Sentry (optional) ────────────────────────────────────────────────────────
// Not wired up. The @sentry/react dependency was dropped alongside the
// backend one, so enabling this means `pnpm add @sentry/react` in
// packages/frontend first, then uncommenting the block below. It also needs
// VITE_SENTRY_DSN at build time.
//
// import * as Sentry from '@sentry/react';
// if (import.meta.env.VITE_SENTRY_DSN) {
//   Sentry.init({
//     dsn: import.meta.env.VITE_SENTRY_DSN,
//     environment: import.meta.env.MODE,
//     tracesSampleRate: 0.1,
//   });
// }

// Inject the shared QueryClient into router context so `beforeLoad` guards
// can `ensureQueryData(sessionQueryOptions())` without importing the client
// directly — keeps the router decoupled from the query layer's singleton.
const router = createRouter({
  routeTree,
  context: { queryClient } satisfies RouterContext,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found.');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
